import { useEffect, useRef, useState } from 'react';

export interface AreaPoint {
  /** UTC day, "2026-11-20". */
  day: string;
  value: number;
}

const HEIGHT = 220;
const PAD = { top: 12, right: 12, bottom: 26, left: 64 };
const DAY_MS = 86_400_000;

/** Width of the element, following resizes (no ResizeObserver: a fixed fallback). */
function useWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(240, Math.floor(entry.contentRect.width)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/** Rounds up to 1, 2 or 5 × a power of ten, so the axis reads in round numbers. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return ([1, 2, 5, 10].find((m) => m * p >= v) ?? 10) * p;
}

const dayLabel = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

/**
 * One area in the accent on a hairline grid (DESIGN.md → Charts), from zero up, by date. Days
 * without a point are bridged by a straight line.
 */
export function AreaChart({
  points,
  from,
  to,
  format,
  label,
}: {
  points: AreaPoint[];
  /** The range shown, as UTC days. */
  from: string;
  to: string;
  /** Axis labels. */
  format: (v: number) => string;
  label: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>(640);
  const t0 = Date.parse(`${from}T00:00:00Z`);
  const t1 = Math.max(Date.parse(`${to}T00:00:00Z`), t0 + DAY_MS);
  const max = niceMax(Math.max(0, ...points.map((p) => p.value)));
  const w = width - PAD.left - PAD.right;
  const h = HEIGHT - PAD.top - PAD.bottom;
  const x = (day: string) => PAD.left + ((Date.parse(`${day}T00:00:00Z`) - t0) / (t1 - t0)) * w;
  const y = (v: number) => PAD.top + h - (v / max) * h;
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.day)},${y(p.value)}`).join(' ');
  const area =
    points.length > 1
      ? `${line} L${x(points.at(-1)!.day)},${y(0)} L${x(points[0]!.day)},${y(0)} Z`
      : '';
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const midDay = new Date((t0 + t1) / 2).toISOString().slice(0, 10);

  return (
    <div ref={ref} className="w-full">
      <svg width={width} height={HEIGHT} role="img" aria-label={label} className="block">
        {ticks.map((v) => (
          <g key={v}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={y(v)}
              y2={y(v)}
              className="stroke-line"
              strokeWidth={1}
            />
            <text
              x={PAD.left - 8}
              y={y(v)}
              textAnchor="end"
              dominantBaseline="middle"
              className="fill-muted font-mono text-tag"
            >
              {format(v)}
            </text>
          </g>
        ))}
        {[from, midDay, to].map((d, i) => (
          <text
            key={d}
            x={x(d)}
            y={HEIGHT - 8}
            textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
            className="fill-muted font-mono text-tag"
          >
            {dayLabel(d)}
          </text>
        ))}
        {area && <path d={area} className="fill-accent" fillOpacity={0.15} />}
        {points.length > 1 && (
          <path d={line} fill="none" className="stroke-accent" strokeWidth={2} />
        )}
        {points.map((p) => (
          <circle key={p.day} cx={x(p.day)} cy={y(p.value)} r={2.5} className="fill-accent">
            <title>
              {dayLabel(p.day)}: {format(p.value)}
            </title>
          </circle>
        ))}
      </svg>
    </div>
  );
}
